/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Star, Linkedin, Youtube, Instagram, PlayCircle, GraduationCap, BookOpen, Award, ArrowRight, Film, Play } from "lucide-react";
import VideoClipModal from "@/components/VideoClipModal";

const displayStack = { fontFamily: "'Outfit', system-ui, sans-serif" };

export default function FacultyProfile() {
  const { slug } = useParams();
  const [f, setF] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeClip, setActiveClip] = useState(null);

  useEffect(() => {
    setF(null); setNotFound(false);
    window.scrollTo({ top: 0, behavior: "instant" });
    api.get(`/faculty/${slug}`)
      .then(({ data }) => setF(data))
      .catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!f) return;
    document.title = `${f.full_name} — ${f.designation || "Faculty"} | CodeWithKamlesh`;
    setMeta("description", (f.bio || `${f.full_name} — Expert faculty at CodeWithKamlesh, ${f.experience_years || 0}+ years experience`).slice(0, 158));
    setJsonLd(f);
    return () => setJsonLd(null);
  }, [f]);

  if (notFound) {
    return (
      <div className="min-h-[60vh] bg-[#050505] text-white flex items-center justify-center p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold" style={displayStack}>Faculty not found</h1>
          <Link to="/" className="mt-4 inline-block text-[#FF0066]">← Back to home</Link>
        </div>
      </div>
    );
  }
  if (!f) return <div className="bg-[#050505] text-white min-h-screen flex items-center justify-center"><div className="animate-pulse text-white/40">Loading…</div></div>;

  const social = f.social_links || {};
  return (
    <div className="bg-[#050505] text-white min-h-screen pb-20" data-testid="faculty-profile">
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-[#FF0066]/15 rounded-full blur-[140px] pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <div className="grid md:grid-cols-12 gap-8">
          {/* Photo */}
          <div className="md:col-span-4">
            <div className="aspect-square bg-gradient-to-br from-[#FF0066]/30 via-[#FF0066]/10 to-[#050505] rounded-2xl overflow-hidden border border-white/10">
              {f.profile_image ? (
                <img src={f.profile_image} alt={f.full_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <GraduationCap className="w-24 h-24 text-[#FF0066]/40" />
                </div>
              )}
            </div>
            {/* Socials */}
            <div className="mt-4 flex items-center justify-center gap-3">
              {social.linkedin && <a href={social.linkedin} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-blue-500/10 hover:bg-blue-500/20 border border-blue-400/30 flex items-center justify-center text-blue-400 transition"><Linkedin className="w-4 h-4" /></a>}
              {social.youtube  && <a href={social.youtube}  target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-red-500/10 hover:bg-red-500/20 border border-red-400/30 flex items-center justify-center text-red-400 transition"><Youtube  className="w-4 h-4" /></a>}
              {social.instagram&& <a href={social.instagram} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-pink-500/10 hover:bg-pink-500/20 border border-pink-400/30 flex items-center justify-center text-pink-400 transition"><Instagram className="w-4 h-4" /></a>}
            </div>
          </div>

          {/* Info */}
          <div className="md:col-span-8">
            <h1 className="text-3xl md:text-4xl font-extrabold text-white" style={displayStack}>{f.full_name}</h1>
            {f.designation && <div className="mt-2 text-base text-[#FF0066] font-medium">{f.designation}</div>}
            {f.specialization && <div className="mt-1.5 text-sm text-white/65 leading-snug">{f.specialization}</div>}

            <div className="mt-4 flex flex-wrap gap-4 text-sm text-white/75">
              {f.experience_years > 0 && (
                <span className="inline-flex items-center gap-1.5"><BookOpen className="w-4 h-4 text-[#FF0066]" /> {f.experience_years}+ years experience</span>
              )}
              {f.qualification && (
                <span className="inline-flex items-center gap-1.5"><GraduationCap className="w-4 h-4 text-[#FF0066]" /> {f.qualification}</span>
              )}
              {f.rating > 0 && (
                <span className="inline-flex items-center gap-1.5"><Star className="w-4 h-4 text-amber-400 fill-current" /> {f.rating} {f.review_count > 0 && <span className="text-white/50">({f.review_count} reviews)</span>}</span>
              )}
            </div>

            {f.bio && <p className="mt-5 text-sm md:text-base text-white/80 leading-relaxed">{f.bio}</p>}

            {Array.isArray(f.achievements) && f.achievements.length > 0 && (
              <div className="mt-6">
                <h3 className="text-xs uppercase tracking-wider text-white/50 font-bold mb-2">Achievements</h3>
                <div className="flex flex-wrap gap-2">
                  {f.achievements.map((a, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/25 text-xs text-emerald-300"><Award className="w-3 h-3" /> {a}</span>
                  ))}
                </div>
              </div>
            )}

            {f.demo_lecture_url && (
              <a href={f.demo_lecture_url} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 bg-[#FF0066]/15 hover:bg-[#FF0066]/25 border border-[#FF0066]/40 text-[#FF0066] font-semibold rounded-full px-5 py-2.5 text-sm transition">
                <PlayCircle className="w-4 h-4" /> Watch demo lecture <ArrowRight className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>

        {/* ============ TEACHING CLIPS ============ */}
        {Array.isArray(f.clips) && f.clips.length > 0 && (
          <section className="mt-14" data-testid="faculty-clips-section">
            <div className="flex items-end justify-between mb-5">
              <div>
                <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-fuchsia-300 font-bold">
                  <Film className="w-3.5 h-3.5" /> Watch how I teach
                </div>
                <h2 className="text-2xl md:text-3xl font-bold mt-1.5" style={displayStack}>
                  {f.clips.length} short clip{f.clips.length > 1 ? "s" : ""} from real classes
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {f.clips
                .slice()
                .sort((a, b) => (a.priority || 100) - (b.priority || 100))
                .map((clip) => (
                  <button
                    key={clip.id}
                    onClick={() => setActiveClip(clip)}
                    className="group relative aspect-video bg-[#0a0a0a] border border-white/10 hover:border-[#FF0066]/50 rounded-xl overflow-hidden text-left transition"
                    data-testid={`clip-tile-${clip.id}`}
                  >
                    {clip.thumbnail ? (
                      <img
                        src={clip.thumbnail}
                        alt={clip.title || "Teaching clip"}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                        onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/30">
                        <Play className="w-12 h-12" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                      <div className="w-14 h-14 rounded-full bg-[#FF0066] flex items-center justify-center shadow-2xl">
                        <Play className="w-6 h-6 text-white fill-current ml-1" />
                      </div>
                    </div>
                    {clip.title && (
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <div className="text-sm font-semibold text-white line-clamp-2">{clip.title}</div>
                      </div>
                    )}
                  </button>
                ))}
            </div>
          </section>
        )}
      </div>

      {/* Video player modal */}
      <VideoClipModal clip={activeClip} onClose={() => setActiveClip(null)} />
    </div>
  );
}

function setMeta(name, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

function setJsonLd(f) {
  let el = document.getElementById("cwk-jsonld-faculty");
  if (!f) { if (el) el.remove(); return; }
  const data = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: f.full_name,
    jobTitle: f.designation,
    description: f.bio,
    image: f.profile_image,
    sameAs: Object.values(f.social_links || {}).filter(Boolean),
    worksFor: { "@type": "Organization", name: "CodeWithKamlesh" },
    knowsAbout: f.specialization?.split(",").map((s) => s.trim()),
  };
  if (!el) {
    el = document.createElement("script");
    el.id = "cwk-jsonld-faculty";
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
